import { ClassName, Col, Position, Row } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { EmployeeGroupPicker, EmployeePicker, ProjectGroupPicker, ProjectPicker } from 'components';
import SidePanelFilter from 'components/foundation/FilterSidePanel/FilterSidePanel';
import BasicNonNullPicker from 'components/foundation/pickers/basic-picker/BasicNonNullPicker/BasicNonNullPicker';
import BasicPicker from 'components/foundation/pickers/basic-picker/BasicPicker/BasicPicker';
import DateRange from 'components/foundation/ranges/DateRange/DateRange';
import { useQueryParams, useTimeRange } from 'hooks';
import useGpsOutsideProjectReportSettingsUpdate from 'hooks/models/member-settings/useGpsOutsideProjectReportSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import { t } from 'i18next';
import { some } from 'lodash';
import { DateTime, Duration } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import IDatePickerValue from 'types/DatePickerValue';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { QueryParam } from 'utils/constants/queryParam';
import { getAdjustedEndDateChange, getAdjustedStartDateChange } from 'utils/dateUtils';
import './GpsOutsideProjectReportSidePanel.scss';

export interface IGpsOutsideProjectReportSidePanelProps {
  className?: ClassName;
  setCustomTimeRange: (timeRange: ITimeRange<DateTime> | null) => void;
}

export enum StatusOptions {
  AUTHORIZED = 'authorized',
  UNAUTHORIZED = 'unauthorized',
  PENDING_REVIEW = 'pending_review',
}

export interface IGpsOutsideProjectReportFilterData {
  memberId: string | null;
  employeeGroupId: string | null;
  currentTimeRangeType: TimeRangeType;
  reviewdByMemberId: string | null;
  projectId: string | null;
  projectGroupId: string | null;
  status: string | null;
  startDate: DateTime;
  endDate: DateTime;
  customTimeRange: ITimeRange<DateTime | null>;
}

const GpsOutsideProjectReportSidePanel = (props: IGpsOutsideProjectReportSidePanelProps) => {
  const { className, setCustomTimeRange } = props;

  const classes = classNames('gps-outside-project-side-panel mb-5', className);

  const memberSettings = useMemberSettings();
  const updateGpsOutsideProjectReportSettings = useGpsOutsideProjectReportSettingsUpdate();

  const dateRangeOptions = useRef([
    { id: TimeRangeType.PAY_PERIOD, name: t('Pay Period') },
    { id: TimeRangeType.MONTHLY, name: t('Monthly') },
    { id: TimeRangeType.WEEKLY, name: t('Weekly') },
    { id: TimeRangeType.DAILY, name: t('Daily') },
    { id: TimeRangeType.CUSTOM, name: t('Custom') },
  ]);

  const today = useRef<DateTime>(DateTime.local().startOf('day'));
  const { getParam, updateParams } = useQueryParams();
  const startDate = getParam(QueryParam.START_DATE);
  const endDate = getParam(QueryParam.END_DATE);
  const currentDateRangeType =
    getParam<TimeRangeType>(QueryParam.TIME_RANGE_TYPE) ??
    memberSettings?.web?.features?.gpsOutsideProjectReport?.timeRangeType ??
    TimeRangeType.DAILY;
  const payPeriodRange = useTimeRange(TimeRangeType.PAY_PERIOD);
  const currentMemberId = getParam(QueryParam.EMPLOYEE_ID);
  const currentStatus = getParam(QueryParam.FLAGGED_LOCATION_STATUS);
  const currentProjectId = getParam(QueryParam.PROJECT_ID);
  const currentEmployeeGroupId = getParam(QueryParam.EMPLOYEE_GROUP_ID);
  const currentProjectGroupId = getParam(QueryParam.PROJECT_GROUP_ID);
  const currentReviewedByMemberId = getParam(QueryParam.REVIEWED_BY_MEMBER_ID);
  const currentStartDate = useRef<DateTime>(
    startDate !== null ? DateTime.fromISO(startDate!).startOf('day') : today.current
  );
  const currentEndDate = useRef<DateTime>(
    endDate !== null ? DateTime.fromISO(endDate!).endOf('day') : today.current.endOf('day')
  );
  const currentPayPeriodEndTime = useMemo<DateTime>(() => payPeriodRange.timeRange.endTime, [payPeriodRange]);

  useEffect(() => {
    if (startDate || endDate) {
      currentStartDate.current = DateTime.fromISO(startDate!).startOf('day');
      currentEndDate.current = DateTime.fromISO(endDate!).endOf('day');
      setData((currentData) => {
        return {
          ...currentData,
          startDate: currentStartDate.current,
          endDate: currentEndDate.current,
        };
      });
    }
  }, [startDate, endDate]);

  const [data, setData] = useState<IGpsOutsideProjectReportFilterData>({
    memberId: currentMemberId,
    employeeGroupId: currentEmployeeGroupId,
    currentTimeRangeType: currentDateRangeType,
    reviewdByMemberId: currentReviewedByMemberId,
    projectId: currentProjectId,
    projectGroupId: currentProjectGroupId,
    status: currentStatus,
    startDate: currentStartDate.current,
    endDate: currentEndDate.current,
    customTimeRange: {
      startTime: startDate ? DateTime.fromISO(startDate).startOf('day') : DateTime.local().startOf('day'),
      endTime: endDate
        ? DateTime.fromISO(endDate).endOf('day').set({ millisecond: 0 })
        : DateTime.local().endOf('day').set({ millisecond: 0 }),
    },
  });

  const onFilterApply = () => {
    updateParams({
      [QueryParam.START_DATE]: data.startDate.toISODate() ?? today.current,
      [QueryParam.END_DATE]: data.endDate.toISODate() ?? today.current,
      [QueryParam.FLAGGED_LOCATION_STATUS]: data.status ?? null,
      [QueryParam.EMPLOYEE_ID]: data.memberId ?? null,
      [QueryParam.PROJECT_ID]: data.projectId ?? null,
      [QueryParam.REVIEWED_BY_MEMBER_ID]: data.reviewdByMemberId ?? null,
      [QueryParam.EMPLOYEE_GROUP_ID]: data.employeeGroupId ?? null,
      [QueryParam.PROJECT_GROUP_ID]: data.projectGroupId ?? null,
      [QueryParam.TIME_RANGE_TYPE]: data.currentTimeRangeType ?? null,
    });
    currentStartDate.current = data.startDate;
    currentEndDate.current = data.endDate;
    updateCustomRange(data);
    if (data.currentTimeRangeType !== TimeRangeType.CUSTOM) {
      updateGpsOutsideProjectReportSettings([
        { key: 'timeRangeType', payload: data.currentTimeRangeType ?? TimeRangeType.DAILY },
      ]);
    }
  };

  const onFilterClear = () => {
    setData({
      ...data,
      startDate: today.current,
      endDate: today.current,
      memberId: null,
      employeeGroupId: null,
      reviewdByMemberId: null,
      projectId: null,
      projectGroupId: null,
      status: null,
      customTimeRange: {
        startTime: DateTime.local().startOf('day'),
        endTime: DateTime.local().endOf('day').set({ millisecond: 0 }),
      },
    });
    updateParams({
      [QueryParam.START_DATE]: today.current.toISODate(),
      [QueryParam.END_DATE]: today.current.toISODate(),
      [QueryParam.FLAGGED_LOCATION_STATUS]: null,
      [QueryParam.EMPLOYEE_ID]: null,
      [QueryParam.PROJECT_ID]: null,
      [QueryParam.REVIEWED_BY_MEMBER_ID]: null,
      [QueryParam.EMPLOYEE_GROUP_ID]: null,
      [QueryParam.PROJECT_GROUP_ID]: null,
    });
  };

  const anyFilterSet = () => {
    return some(
      Object.keys(data).filter(
        (k) => k !== 'startDate' && k !== 'endDate' && k !== 'currentTimeRangeType' && k !== 'customTimeRange'
      ),
      (key: keyof IGpsOutsideProjectReportFilterData) => data[key] !== null
    );
  };

  const updateCustomRange = (data: IGpsOutsideProjectReportFilterData) => {
    if (
      data.currentTimeRangeType === TimeRangeType.CUSTOM &&
      data.customTimeRange.startTime &&
      data.customTimeRange.endTime
    ) {
      setCustomTimeRange(data.customTimeRange as ITimeRange<DateTime>);
    } else if (data.currentTimeRangeType === TimeRangeType.CUSTOM) {
      const startTime = data.customTimeRange.startTime ?? DateTime.utc().startOf('day');
      const endTime = data.customTimeRange.endTime ?? DateTime.utc().endOf('day');
      setCustomTimeRange({ startTime, endTime });
      setData({ ...data, customTimeRange: { startTime, endTime } });
    } else {
      setCustomTimeRange(null);
    }
  };

  const getDisabledDateRange = () => {
    const clampDuration = Duration.fromObject({ years: 3 });
    const maxEndTime = currentPayPeriodEndTime.plus(clampDuration);

    return {
      disabledAfter: maxEndTime < currentPayPeriodEndTime ? maxEndTime : currentPayPeriodEndTime,
      disabledBefore: data.customTimeRange.endTime
        ? data.customTimeRange.endTime.minus(clampDuration)
        : currentPayPeriodEndTime.minus(clampDuration),
    };
  };

  const handleStartDateChange = (value: IDatePickerValue) => {
    if (value.value) {
      const { newStartDate, newEndDate } = getAdjustedStartDateChange(value.value, data.customTimeRange.endTime);

      setData({
        ...data,
        customTimeRange: {
          startTime: newStartDate,
          endTime: newEndDate,
        },
      });
    } else {
      setData({
        ...data,
        customTimeRange: {
          ...data.customTimeRange,
          startTime: null,
        },
      });
    }
  };

  const handleEndDateChange = (endDatePickerValue: IDatePickerValue) => {
    if (endDatePickerValue.value) {
      const { newStartDate, newEndDate } = getAdjustedEndDateChange(
        endDatePickerValue.value,
        data.customTimeRange.startTime
      );

      setData({
        ...data,
        customTimeRange: {
          startTime: newStartDate ?? data.customTimeRange.startTime,
          endTime: newEndDate,
        },
      });
    } else {
      setData({
        ...data,
        customTimeRange: {
          ...data.customTimeRange,
          endTime: null,
        },
      });
    }
  };

  const appliableChanges = () => {
    return (
      currentStartDate.current !== data.startDate ||
      currentEndDate.current !== data.endDate ||
      currentMemberId !== data.memberId ||
      currentDateRangeType !== data.currentTimeRangeType ||
      currentStatus !== data.status ||
      currentProjectId !== data.projectId ||
      currentReviewedByMemberId !== data.reviewdByMemberId ||
      currentEmployeeGroupId !== data.employeeGroupId ||
      currentProjectGroupId !== data.projectGroupId
    );
  };

  const dateRangePicker = () => {
    return (
      <BasicNonNullPicker
        onSelect={(dateRangeType: TimeRangeType) => {
          const startDate =
            dateRangeType === TimeRangeType.PAY_PERIOD ? payPeriodRange.timeRange.startTime : today.current;
          const endDate = dateRangeType === TimeRangeType.PAY_PERIOD ? payPeriodRange.timeRange.endTime : today.current;
          setData({ ...data, currentTimeRangeType: dateRangeType, startDate, endDate });
        }}
        value={data.currentTimeRangeType}
        data={dateRangeOptions.current}
      />
    );
  };

  const statusOptions = () => {
    return [
      {
        name: t('Authorized'),
        id: StatusOptions.AUTHORIZED,
      },
      {
        name: t('Unauthorized'),
        id: StatusOptions.UNAUTHORIZED,
      },
      {
        name: t('Pending Review'),
        id: StatusOptions.PENDING_REVIEW,
      },
    ];
  };

  const statusPicker = () => (
    <Row className="mt-5">
      <Col>
        <BasicPicker
          data={statusOptions()}
          onSelect={(status) => {
            setData({ ...data, status });
          }}
          placeholder={t('Status')}
          value={data.status}
          className="status-selector"
        />
      </Col>
    </Row>
  );

  return (
    <SidePanelFilter
      onApply={onFilterApply}
      onClear={onFilterClear}
      clearable={anyFilterSet()}
      appliable={appliableChanges()}
      buttonsClassName={'px-5'}
      className={classes}
    >
      <div className="px-5 py-4">
        <h3 className="mb-4">{t('Settings')}</h3>
        {dateRangePicker()}
        {data.currentTimeRangeType === TimeRangeType.CUSTOM && (
          <DateRange
            className="pt-4"
            startDate={data.customTimeRange.startTime}
            endDate={data.customTimeRange.endTime}
            onEndChange={handleEndDateChange}
            onStartChange={handleStartDateChange}
            {...getDisabledDateRange()}
          />
        )}
        <h3 className="my-4">{t('Filter')}</h3>
        {statusPicker()}
        <EmployeePicker
          onSelect={(memberId) => {
            setData({ ...data, memberId });
          }}
          value={data.memberId}
          placeholder={t('Employee')}
          position={Position.BOTTOM_END}
          className="mt-5"
        />
        <EmployeeGroupPicker
          onSelect={(employeeGroupId) => {
            setData({ ...data, employeeGroupId });
          }}
          value={data.employeeGroupId}
          placeholder={t('Employee Group')}
          position={Position.BOTTOM_END}
          className="mt-5"
        />
        <ProjectPicker
          placeholder={t('Project')}
          value={data.projectId}
          onSelect={(projectId) => {
            setData({ ...data, projectId });
          }}
          position={Position.BOTTOM_END}
          className="mt-5"
        />
        <ProjectGroupPicker
          onSelect={(projectGroupId) => {
            setData({ ...data, projectGroupId });
          }}
          value={data.projectGroupId}
          placeholder={t('Project Group')}
          position={Position.BOTTOM_END}
          className="mt-5"
        />
        <EmployeePicker
          onSelect={(reviewdByMemberId) => {
            setData({ ...data, reviewdByMemberId });
          }}
          value={data.reviewdByMemberId}
          placeholder={t('Reviewed By')}
          position={Position.BOTTOM_END}
          className="mt-5"
        />
      </div>
    </SidePanelFilter>
  );
};

export default GpsOutsideProjectReportSidePanel;
